import { Heading, HeadingLevel } from "@ariakit/react";
import cn from "clsx";

import type { ArticleStyle } from "../../../types/content";
import type { Header } from "../../../types/content/feedItem";

import { isFeedItemVignette } from "../../../utils/typechecks";
import styles from "./TeaserClusterHeader.module.scss";
import { TeaserClusterVignette } from "./TeaserClusterVignette/TeaserClusterVignette";

interface ItemProps {
  text?: string;
  style: ArticleStyle;
}

function Overline({ text, style }: ItemProps) {
  if (!text) {
    return null;
  }

  return <div className={cn([styles.overline, styles[style]])}>{text}</div>;
}

function Title({ text, style }: ItemProps) {
  if (!text) {
    return null;
  }

  return (
    <HeadingLevel>
      <Heading className={cn([styles.title, styles[style]])}>{text}</Heading>
    </HeadingLevel>
  );
}

function Intro({ text, style }: ItemProps) {
  if (!text) {
    return null;
  }

  return <div className={cn([styles.intro, styles[style]])}>{text}</div>;
}

interface TeaserClusterHeaderProps {
  items: Header;
  style: ArticleStyle;
  isSmallCluster?: boolean;
  isBox?: boolean;
  teaserWeightValue?: number | null;
  isLoggedIn?: boolean;
  articleId?: string;
}

function TeaserClusterHeader({
  items,
  style,
  isSmallCluster,
  isBox,
  teaserWeightValue,
  isLoggedIn,
  articleId,
}: TeaserClusterHeaderProps) {
  if (!items) {
    return null;
  }

  return (
    <div
      className={cn([
        styles.headerContainer,
        styles[style],
        isSmallCluster && styles.smallCluster,
        isBox && styles.box,
      ])}
      data-density-score={teaserWeightValue} // For debugging
    >
      <>
        {items.content.map((item, index) => {
          if (item.type === "Overline") {
            return <Overline key={index} text={item.value} style={style} />;
          }
          if (item.type === "Title") {
            return <Title key={index} text={item.value} style={style} />;
          }
          if (item.type === "Intro") {
            return <Intro key={index} text={item.value} style={style} />;
          }
          if (isFeedItemVignette(item)) {
            return (
              <TeaserClusterVignette
                key={index}
                resource={item}
                isNative={style === "native"}
                isPremium={style === "premium"}
                isLoggedIn={isLoggedIn}
                articleId={articleId}
              />
            );
          }

          return null;
        })}
      </>
    </div>
  );
}

export { TeaserClusterHeader };
